/**
 * Bottom HUD geometry for hudQueue.ts, in unscaled units (multiply by the HUD scale):
 * the queue bar, the key rows beside it, and where the HP bar fits. Kept free of DOM
 * code so the tests can import it.
 */

export const SLOT = 56, GAP = 8, KEY = 40, KEY_GAP = 6;
/** Queue bar width, one row of four key tiles, and the space between the queue and each key row. */
export const QUEUE_W = SLOT * 5 + GAP * 4, KEY_ROW_W = KEY * 4 + KEY_GAP * 3, KEY_SIDE_GAP = 14;
/** HP bar width and height, the narrowest it shrinks to, and the clear space it keeps from its neighbour on the right. */
export const HP_W = 320, HP_H = 18, HP_MIN_W = 120, HP_GAP = 16;

/**
 * Where the HP bar goes (its top left corner and width), measured from the unshaken centre.
 * It sits bottom left and stops a gap short of its neighbour on that line: the Q W E R key
 * tiles on desktop, or the queue's first slot when touch controls hide the keys. When the
 * keys leave it less than its narrowest width (portrait or very narrow desktop windows), it
 * moves up above the key tiles and only keeps clear of the queue.
 */
export function hpBarLayout(w: number, h: number, s: number, keysShown: boolean): { x: number; y: number; width: number } {
  const x = 20 * s;
  const fit = (room: number): number => Math.max(HP_MIN_W * s, Math.min(HP_W * s, room));
  const queueLeft = w / 2 - QUEUE_W * s / 2;
  const besideQueue = queueLeft - HP_GAP * s - x;
  const low = h - (20 + 30 + HP_H) * s; // status icons and names go in the 30 units under the bar
  if (!keysShown) return { x, y: low, width: fit(besideQueue) };
  const besideKeys = queueLeft - (KEY_SIDE_GAP + KEY_ROW_W + HP_GAP) * s - x;
  if (besideKeys >= HP_MIN_W * s) return { x, y: low, width: fit(besideKeys) };
  const keyTop = h - (24 + SLOT / 2 + KEY / 2) * s;
  return { x, y: keyTop - (HP_H + 34) * s, width: fit(besideQueue) };
}

/** The cast preview card over the queue (its width is CARD_W * 1.3) and the Magick pill above it, at its widest. */
export const CARD_W = 150, CARD_H = 64, PILL_H = 36, PILL_MAX_W = 330;
/** Half the centre column the pill may use, plus a gap: the hint panel keeps out of it. */
const PILL_COLUMN = PILL_MAX_W / 2 + 8;

/** Where the queue bar's top edge is (the preview card sits 10 units above it). */
export function queueTop(h: number, s: number): number {
  return h - (24 + SLOT) * s;
}

/**
 * The Magick pill: the Magick the queue spells and the key that casts it (or its cooldown).
 * It stands centred over the cast preview card, where the eye already is while queueing, so
 * neither the key rows beside the queue nor a phone's buttons on the right ever cover it.
 */
export function magickPillLayout(w: number, h: number, s: number, pillW: number): { x: number; y: number; w: number; h: number } {
  const cardTop = queueTop(h, s) - (10 + CARD_H) * s;
  return { x: w / 2 - pillW / 2, y: cardTop - (6 + PILL_H) * s, w: pillW, h: PILL_H * s };
}

/**
 * The Magick hint panel (the Magicks the queue is the start of): its top left and the widest it
 * may be. Bottom right when it clears the right-hand key row; in a narrower window (4:3) the key
 * row reaches that corner, so the panel stands just above it instead, right of the pill's column.
 * With touch controls the buttons own the right of the screen, so the panel goes bottom left,
 * above the HP bar, and stops short of the pill and the preview card.
 */
export function magickHintLayout(w: number, h: number, s: number, pw: number, ph: number, touch: boolean): { x: number; y: number; maxW: number } {
  if (touch) {
    const hp = hpBarLayout(w, h, s, false);
    return { x: hp.x, y: hp.y - 12 * s - ph, maxW: w / 2 - PILL_COLUMN * s - hp.x };
  }
  const x = w - pw - 16 * s;
  const keysRight = w / 2 + (QUEUE_W / 2 + KEY_SIDE_GAP + KEY_ROW_W) * s;
  if (x >= keysRight + 16 * s) return { x, y: h - ph - 16 * s, maxW: pw };
  const keyTop = h - (24 + SLOT / 2 + KEY / 2) * s;
  const lifted = Math.max(x, w / 2 + PILL_COLUMN * s);
  return { x: lifted, y: keyTop - 12 * s - ph, maxW: Math.min(pw, w - 16 * s - lifted) };
}

/** The two stash pods (docs/spells-depth.md): small squares and the gap between them. */
export const POD = 32, POD_GAP = 6;

/**
 * The stash pods sit just above the queue's left end, reaching a little past it, and stop
 * short of the cast preview card over the queue's middle: clear of the key rows on desktop,
 * and of the HP bar and the Magick hint panel on a phone.
 */
export function stashPodsLayout(w: number, h: number, s: number): { x: number; y: number; size: number }[] {
  const queueLeft = w / 2 - (QUEUE_W * s) / 2;
  const y = queueTop(h, s) - (8 + POD) * s;
  const x0 = queueLeft - 12 * s;
  return [0, 1].map((i) => ({ x: x0 + i * (POD + POD_GAP) * s, y, size: POD * s }));
}

/** The weave pill: where the Magick pill goes, or one pill higher while the Magick pill shows. */
export function weavePillLayout(w: number, h: number, s: number, pillW: number, stacked: boolean): { x: number; y: number; w: number; h: number } {
  const m = magickPillLayout(w, h, s, pillW);
  return stacked ? { ...m, y: m.y - (PILL_H + 6) * s } : m;
}

/** The room panel (top left, Panels.drawWave) and the width of the top-right buttons' corner, in unscaled units. */
export const ROOM_PANEL = { x: 16, y: 16, w: 230, h: 74 } as const;
export const CORNER_W = 130;
/** Capture the flag: the score bar, the scoreboard's width (and a phone's), the minimap's width on a phone. */
export const CTF_BAR_W = 300, CTF_BAR_H = 44, BOARD_W = 210, BOARD_NARROW_W = 180, MAP_NARROW_W = 150;

export interface CtfHudLayout {
  bar: { x: number; y: number; w: number; h: number };
  /** The scoreboard's top left and width. */
  board: { x: number; y: number; w: number };
  /** The minimap's left edge, top (null: just under the scoreboard) and width. */
  map: { x: number; y: number | null; w: number };
}

/**
 * Capture the flag's HUD. With room between the room panel and the buttons, the score bar sits
 * there (centred on the screen when it can be) and the scoreboard and minimap stand in the ally
 * cards' place under the buttons. In a narrow window there is no such room: the bar goes under the
 * room panel and a narrower scoreboard and a smaller map under it on the right. With touch
 * controls (a phone held sideways) the buttons own the right of the screen below the scoreboard,
 * so the small map goes on the left, under the room panel (or under the bar).
 */
export function ctfHudLayout(w: number, s: number, touch = false): CtfHudLayout {
  const left = (ROOM_PANEL.x + ROOM_PANEL.w + 8) * s, right = w - CORNER_W * s;
  const barW = CTF_BAR_W * s, barH = CTF_BAR_H * s;
  const wide = right - left >= barW;
  const below = (ROOM_PANEL.y + ROOM_PANEL.h + 8) * s;
  const bar = wide
    ? { x: Math.min(Math.max(left, w / 2 - barW / 2), right - barW), y: 10 * s, w: barW, h: barH }
    : { x: ROOM_PANEL.x * s, y: below, w: Math.min(barW, w - 2 * ROOM_PANEL.x * s), h: barH };
  const board = wide
    ? { x: w - (BOARD_W + 16) * s, y: (10 + CTF_BAR_H + 6) * s, w: BOARD_W * s }
    : { x: w - (BOARD_NARROW_W + 16) * s, y: below + barH + 8 * s, w: BOARD_NARROW_W * s };
  const map = touch
    ? { x: ROOM_PANEL.x * s, y: wide ? below : below + barH + 8 * s, w: MAP_NARROW_W * s }
    : { x: board.x + board.w - (wide ? BOARD_W : MAP_NARROW_W) * s, y: null, w: (wide ? BOARD_W : MAP_NARROW_W) * s };
  return { bar, board, map };
}

/** The global Magick badges (Rain, Blizzard, Time Warp): a ring of radius BADGE_R, its label under it, one every BADGE_STEP. */
export const BADGE_R = 16, BADGE_STEP = 70;

/**
 * The centre of the first of `n` global Magick badges. Beside the room panel, as always; in
 * capture the flag the score bar can stand right there, so the row goes centred under it (and
 * from its left end in a narrow window, where the scoreboard sits to the right of it).
 */
export function globalBadgesLayout(w: number, s: number, n: number, ctf: boolean, touch = false): { x: number; y: number } {
  if (!ctf) return { x: (ROOM_PANEL.x + ROOM_PANEL.w + 12 + BADGE_R) * s, y: (ROOM_PANEL.y + 20) * s };
  const { bar } = ctfHudLayout(w, s, touch);
  const y = bar.y + bar.h + (8 + BADGE_R) * s;
  const narrow = bar.y > 10 * s + 1e-6;
  if (narrow) return { x: bar.x + 30 * s, y };
  const row = (n - 1) * BADGE_STEP * s;
  return { x: bar.x + bar.w / 2 - row / 2, y };
}
